/**
 * Super-admin contact import with real progress.
 *
 * Imports every Respectly tenant into a chosen account as a contact (one per
 * account owner), used to seed a marketing account. The backend streams NDJSON
 * progress events over the same response when asked via
 * `Accept: application/x-ndjson` — one line per event, ending with a terminal
 * `result` (success) or `error`. This module hides the transport: callers get an
 * `onProgress` callback plus the final result, and a backend that doesn't stream
 * transparently falls back to the legacy single-JSON path.
 *
 * Mirrors the tenant-app importer (apps/user-tenant/src/lib/importContacts.ts);
 * the only differences are a JSON body (no multipart) and the super-admin
 * endpoint.
 */
import { API_ENDPOINTS } from '@respectly/api-client';

export interface ImportSummary {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  autoGrantedInformational?: number;
  requiresManualConsent?: number;
}

export interface ImportError {
  row: number;
  field: string;
  value: string;
  message: string;
}

export interface ImportProgressEvent {
  processed: number;
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
}

export interface ImportApiError {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

export type ImportResult =
  | { ok: true; data: { summary: ImportSummary; errors: ImportError[] } }
  | { ok: false; status: number; error: ImportApiError };

const API_URL = import.meta.env.VITE_API_URL ?? '';

/** Import all Respectly tenants into `tenantId` as contacts. */
export async function importAllTenantsToAccount(opts: {
  tenantId: string;
  getToken: () => Promise<string | null>;
  includeSuspended?: boolean;
  onProgress?: (e: ImportProgressEvent) => void;
  /** Fires when response headers arrive: true if the backend streams NDJSON. */
  onResponseStart?: (streaming: boolean) => void;
}): Promise<ImportResult> {
  const token = await opts.getToken();
  if (!token) {
    return {
      ok: false,
      status: 401,
      error: { code: 'UNAUTHORIZED', message: 'Please refresh and try again.' },
    };
  }

  const res = await fetch(
    `${API_URL}${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.IMPORT_CONTACTS(opts.tenantId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'application/x-ndjson',
      },
      body: JSON.stringify({
        source: 'platform_owners',
        options: { includeSuspended: opts.includeSuspended ?? false },
      }),
    }
  );

  return readImportResponse(res, opts.onProgress, opts.onResponseStart);
}

async function readImportResponse(
  res: Response,
  onProgress?: (e: ImportProgressEvent) => void,
  onResponseStart?: (streaming: boolean) => void
): Promise<ImportResult> {
  const contentType = res.headers.get('content-type') ?? '';
  const streaming = contentType.includes('application/x-ndjson');
  onResponseStart?.(streaming);

  // Pre-stream errors (401/400/403/404/500) and older backends that don't
  // stream both answer with plain JSON.
  if (!streaming) {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        error: (body as { error?: ImportApiError }).error ?? {},
      };
    }
    return {
      ok: true,
      data: (body as { data: { summary: ImportSummary; errors: ImportError[] } }).data,
    };
  }

  if (!res.body) throw new Error('Streaming is not supported by this browser');

  let final: ImportResult | null = null;

  const handleLine = (line: string) => {
    if (!line.trim()) return;
    let evt: { type?: string } & Record<string, unknown>;
    try {
      evt = JSON.parse(line);
    } catch {
      return; // tolerate a partial/garbled line
    }
    if (evt.type === 'start' || evt.type === 'progress') {
      onProgress?.({
        processed: (evt.processed as number) ?? 0,
        totalRows: (evt.totalRows as number) ?? 0,
        created: (evt.created as number) ?? 0,
        updated: (evt.updated as number) ?? 0,
        skipped: (evt.skipped as number) ?? 0,
        failed: (evt.failed as number) ?? 0,
      });
    } else if (evt.type === 'result') {
      final = { ok: true, data: evt.data as { summary: ImportSummary; errors: ImportError[] } };
    } else if (evt.type === 'error') {
      final = { ok: false, status: 500, error: (evt.error as ImportApiError) ?? {} };
    }
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newlineAt = buffer.indexOf('\n');
    while (newlineAt >= 0) {
      handleLine(buffer.slice(0, newlineAt));
      buffer = buffer.slice(newlineAt + 1);
      newlineAt = buffer.indexOf('\n');
    }
  }
  handleLine(buffer); // server ended without a trailing newline

  if (!final) throw new Error('Connection lost during import');
  return final;
}
